import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Status } from "../model/status";
import { Labeler } from "../model/labeler";

@Injectable({
  providedIn: "root",
})
export class StatusService implements Labeler {
  private apiUrl: string;
  private byIdUrl: string;
  public _currentStatus: BehaviorSubject<Status[]>;
  public selectedStatus: BehaviorSubject<Status | null>;
  private statuses = [
    { id: 1, name: "new" },
    { id: 2, name: "in_progress" },
    { id: 3, name: "completed" },
  ];
  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService
  ) {
    this._currentStatus = new BehaviorSubject<Status[]>([]);
    this.selectedStatus = new BehaviorSubject<Status | null>(null);
    this.apiUrl = `${env.apiBaseUrl}status/`;
    this.byIdUrl = `${env.apiBaseUrl}status/by-id/`;
  }

  public status(token: string): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(this.apiUrl, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receivestatus(response, observer);
          },
          (error) => {
            this.receivestatus(error, observer);
          }
        );
    });
  }

  public createStatus(token: string, status: Status): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(this.apiUrl, status, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receivestatu(response, observer);
          },
          (error) => {
            this.receivestatu(error, observer);
          }
        );
    });
  }

  public updateStatus(token: string, status: Status): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${status.id}`;

    return new Observable<Messages>((observer) => {
      if (!status.id) {
        observer.next(Messages.from({ status: ["status.id.required"] }));
        return;
      }

      this.http
        .put<any>(url, status, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivestatu(response, observer),
          (error) => this.receivestatu(error, observer)
        );
    });
  }

  public deleteStatus(token: string, statusId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${statusId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivestatu(response, observer),
          (error) => this.receivestatu(error, observer)
        );
    });
  }

  public getStatusById(token: string, statusId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${statusId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivestatu(response, observer),
          (error) => this.receivestatu(error, observer)
        );
    });
  }

  private receivestatus(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Status[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ status: ["status.unknown"] }));
        return;
      }
      this._currentStatus.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "login.not.authorized"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "status",
          "status.incomplete"
        )
      );
    }
  }

  private receivestatu(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Status>)?.body;
      if (!data) {
        observer.next(Messages.from({ status: ["status.unknown"] }));
        return;
      }
      this._currentStatus.next([data]);
      observer.next(Messages.EMPTY);
    } else if (response.status === 204) {
      observer.next(Messages.EMPTY);
    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "status",
          "status.load.error."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const statusId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getStatusById(token, Number(statusId)).subscribe();
    } else {
      const emptyStatus = new Status(0, "", "");
      this.select(emptyStatus);
    }
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(status: Status) {
    this.selectedStatus.next(status);
  }

  public get selectedStatusObservable(): Observable<Status | null> {
    return this.selectedStatus.asObservable();
  }

  public get statusObservable(): Observable<Status[]> {
    return this._currentStatus;
  }

  public get statusData(): Status[] {
    return this._currentStatus.value;
  }

  public get currentStatus(): Status {
    const value = this.selectedStatus.value;
    return value || Status.EMPTY;
  }
  public labelForId(id: number): string {
    let label = "";
    if (this.statusData?.length > 0 && id && id > 0) {
      const found = this.statusData.filter((status) => id === status.id);
      if (found.length > 0) {
        label = found[0].name;
      }
    }
    return label;
  }

  public makeLabel(id: number): Observable<string> {
    const observable = new BehaviorSubject("");

    if (this.statusData?.length > 0) {
      const label = this.labelForId(id);

      observable.next(label);
    } else {
      this.status(this.auth.token).subscribe((m) => {
        observable.next(this.labelForId(id));
      });
    }
    return observable;
  }
  getIdByName(name: string): number | null {
    const status = this.statuses.find((status) => status.name === name);
    return status ? status.id : null;
  }
  public colorForId(id: number): string {
    let color = "";
    if (this.statusData?.length > 0) {
      const found = this.statusData.filter((statuses) => id === statuses.id);
      if (found.length > 0) {
        color = found[0].color;
      }
    }
    return color;
  }
  public makeColor(id: number): Observable<string> {
    const observable = new BehaviorSubject("");

    if (this.statusData?.length > 0) {
      const label = this.colorForId(id);
      observable.next(label);
    } else {
      this.status(this.auth.token).subscribe((m) => {
        observable.next(this.colorForId(id));
      });
    }
    return observable;
  }

  public makeBackgroundColor(id: number): Observable<string> {
    return new BehaviorSubject("#FF6347");
  }
  public makeImportance(id: number): Observable<number> {
    return new BehaviorSubject(1);
  }
}
